const __filename = "/lib/tracking.js", __dirname = "/lib";
export default class StorageMonitor {
  constructor(storageType = 'localStorage') {
    this.storageType = storageType
    this.storage = window[storageType]
    this.listeners = new Map() // key -> array of callbacks
    this.globalListeners = [] // callbacks for all keys
    this.isEnabled = true

    // Store original methods
    this.originalSetItem = this.storage.setItem.bind(this.storage)
    this.originalRemoveItem = this.storage.removeItem.bind(this.storage)
    this.originalClear = this.storage.clear.bind(this.storage)

    // Override storage methods
    this.overrideStorageMethods()

    // Listen for cross-tab changes
    this.setupCrossTabListener()
  }

  /** Override native storage methods to intercept changes. */
  overrideStorageMethods() {
    // Override setItem
    this.storage.setItem = (key, value) => {
      if (!this.isEnabled) {
        return this.originalSetItem(key, value)
      }

      const oldValue = this.storage.getItem(key)
      this.originalSetItem(key, value)

      this.notifyListeners({
        type: 'setItem',
        key,
        oldValue,
        newValue: value,
        source: 'same-tab'
      })
    }

    // Override removeItem
    this.storage.removeItem = key => {
      if (!this.isEnabled) {
        return this.originalRemoveItem(key)
      }

      const oldValue = this.storage.getItem(key)
      this.originalRemoveItem(key)

      this.notifyListeners({
        type: 'removeItem',
        key,
        oldValue,
        newValue: null,
        source: 'same-tab'
      })
    }

    // Override clear
    this.storage.clear = () => {
      if (!this.isEnabled) {
        return this.originalClear()
      }

      // Get all keys before clearing
      const keys = []
      const values = []
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i)
        keys.push(key)
        values.push(this.storage.getItem(key))
      }

      this.originalClear()

      // Notify for each cleared item
      keys.forEach((key, index) => {
        this.notifyListeners({
          type: 'clear',
          key,
          oldValue: values[index],
          newValue: null,
          source: 'same-tab'
        })
      })
    }
  }

  /** Setup listener for cross-tab storage events. */
  setupCrossTabListener() {
    globalThis.addEventListener('storage', e => {
      if (e.storageArea === this.storage && this.isEnabled) {
        this.notifyListeners({
          type: e.newValue === null ? 'removeItem' : 'setItem',
          key: e.key,
          oldValue: e.oldValue,
          newValue: e.newValue,
          source: 'cross-tab'
        })
      }
    })
  }

  /**
   * Add a listener for a specific key.
   *
   * @param {string} key - Storage key to listen for (use '*' for all keys)
   * @param {function} callback - Callback function that receives the event object.
   */
  addListener(key, callback) {
    if (typeof callback !== 'function') {
      throw new TypeError('Callback must be a function')
    }

    if (key === '*') {
      this.globalListeners.push(callback)
    } else {
      if (!this.listeners.has(key)) {
        this.listeners.set(key, [])
      }
      this.listeners.get(key).push(callback)
    }
  }

  /**
   * Remove a listener for a specific key.
   *
   * @param {string} key - Storage key.
   * @param {function} callback - Callback function to remove.
   */
  removeListener(key, callback) {
    if (key === '*') {
      const index = this.globalListeners.indexOf(callback)
      if (index !== -1) {
        this.globalListeners.splice(index, 1)
      }
    } else {
      if (this.listeners.has(key)) {
        const callbacks = this.listeners.get(key)
        const index = callbacks.indexOf(callback)
        if (index !== -1) {
          callbacks.splice(index, 1)
          // Clean up empty arrays
          if (callbacks.length === 0) {
            this.listeners.delete(key)
          }
        }
      }
    }
  }

  /**
   * Remove all listeners for a specific key or all listeners.
   *
   * @param {string} key - Storage key (optional, removes all if not provided)
   */
  removeAllListeners(key = null) {
    if (key === null) {
      // Remove all listeners
      this.listeners.clear()
      this.globalListeners = []
    } else if (key === '*') {
      // Remove all global listeners
      this.globalListeners = []
    } else {
      // Remove listeners for specific key
      this.listeners.delete(key)
    }
  }

  /**
   * Notify all relevant listeners about storage changes.
   *
   * @param {object} event - Storage event object.
   */
  notifyListeners(event) {
    // Notify key-specific listeners
    const keyListeners = this.listeners.get(event.key) || []
    keyListeners.forEach(callback => {
      try {
        callback(event)
      } catch (error) {
        console.error('Storage listener error:', error)
      }
    })

    // Notify global listeners
    this.globalListeners.forEach(callback => {
      try {
        callback(event)
      } catch (error) {
        console.error('Storage listener error:', error)
      }
    })
  }

  /**
   * Enable or disable the monitor.
   *
   * @param {boolean} enabled - Whether monitoring should be enabled.
   */
  setEnabled(enabled) {
    this.isEnabled = !!enabled
  }

  /**
   * Check if monitoring is enabled.
   *
   * @returns {boolean} Whether monitoring is enabled.
   */
  isMonitoringEnabled() {
    return this.isEnabled
  }

  /**
   * Get all current storage items.
   *
   * @returns {object} Object with all key-value pairs.
   */
  getAllItems() {
    const items = {}
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i)
      items[key] = this.storage.getItem(key)
    }
    return items
  }

  /**
   * Get listener statistics.
   *
   * @returns {object} Statistics about current listeners.
   */
  getListenerStats() {
    const keyListenerCount = Array.from(this.listeners.values()).reduce(
      (total, listeners) => total + listeners.length,
      0
    )

    return {
      totalListeners: keyListenerCount + this.globalListeners.length,
      keySpecificListeners: keyListenerCount,
      globalListeners: this.globalListeners.length,
      monitoredKeys: Array.from(this.listeners.keys())
    }
  }

  /** Restore original storage methods and clean up. */
  destroy() {
    // Restore original methods
    this.storage.setItem = this.originalSetItem
    this.storage.removeItem = this.originalRemoveItem
    this.storage.clear = this.originalClear

    // Clear all listeners
    this.removeAllListeners()

    // Remove cross-tab listener would require keeping reference
    // For now, just disable monitoring
    this.setEnabled(false)
  }
}
